import { AttachmentDropzoneProps } from '@/components/Attachment/AttachmentDropzone'
import { AttachmentsSchema, FormAttachment } from '@/components/Attachment/schema'
import { filterFileAttachments, filterFiles } from '@/components/Attachment/utils'
import { Attachment, FileAttachment } from '@/utils/fetchers/attachment'
import { cloneDeep, first, flatMap, omit, zipObject } from 'lodash-es'
import { UseFormReturn } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ZodCustomIssue } from 'zod/v3'
import useAttachmentUploadTaskManager from './useAttachmentUploadTaskManager'

export type UseAttachmentsHelperMethods<
	FieldValue extends {
		attachment: FormAttachment
	} = {
		attachment: FormAttachment
	},
> = Pick<UseFormReturn<FieldValue>, 'getValues' | 'setValue'>

export default function useAttachmentsHelper<
	FieldValue extends {
		attachment: FormAttachment
	},
>(methods: UseAttachmentsHelperMethods<FieldValue>) {
	const getAttachments = () => {
		const map = methods.getValues('attachment.map')
		const ids = methods.getValues('attachment.ids')
		return ids.map((id) => map[id]).filter(Boolean) as Attachment[]
	}

	const handleAttachmentUpdate = (id: string, attachment: Attachment) => {
		const map = methods.getValues('attachment.map')
		const ids = methods.getValues('attachment.ids')
		methods.setValue('attachment', {
			ids,
			map: {
				...map,
				[id]: attachment,
			},
		})
	}

	const { addTasks, removeTask, addTask } = useAttachmentUploadTaskManager(handleAttachmentUpdate)

	const handleAttachmentAdd = async (newAttachments: Attachment[]) => {
		const { success, error } = await AttachmentsSchema.safeParseAsync(
			getAttachments().concat(newAttachments)
		)
		if (!success) {
			const firstError = first(error.issues) as ZodCustomIssue | undefined
			if (firstError?.message) {
				toast.error(firstError.message)

				if (firstError.params?.type === 'ONLY_AVAILABLE_IN_JIKE_APP') {
					toast('请在即刻App中重试')
				}
			}

			return
		}

		const attachmentIds = methods.getValues('attachment.ids')
		const attachmentMap = methods.getValues('attachment.map')
		const newAttachmentIds = newAttachments.map((item) => item.id)

		methods.setValue('attachment', {
			ids: attachmentIds.concat(newAttachmentIds),
			map: {
				...attachmentMap,
				...zipObject(newAttachmentIds, newAttachments),
			},
		})

		addTasks(filterFileAttachments(newAttachments))
	}

	const handleAttachmentDelete = (id: string) => {
		const attachmentIds = methods.getValues('attachment.ids')
		const attachmentMap = methods.getValues('attachment.map')
		const newAttachmentIds = attachmentIds.filter((item) => item !== id)
		const newAttachmentMap = omit(attachmentMap, id)

		methods.setValue('attachment', {
			ids: newAttachmentIds,
			map: newAttachmentMap,
		})

		removeTask(id)
	}

	const handleAttachmentSwap = (oldIndex: number, newIndex: number) => {
		const map = methods.getValues('attachment.map')
		const newAttachmentIds = cloneDeep(methods.getValues('attachment.ids'))

		;[newAttachmentIds[newIndex], newAttachmentIds[oldIndex]] = [
			newAttachmentIds[oldIndex],
			newAttachmentIds[newIndex],
		]
		methods.setValue('attachment', {
			ids: newAttachmentIds,
			map,
		})
	}

	const handleAttachmentsDropped: AttachmentDropzoneProps['onDrop'] = async (files: File[]) => {
		const attachmentGroup = filterFiles(files)
		const newAttachments = flatMap(attachmentGroup)

		await handleAttachmentAdd(newAttachments)
	}

	const handlePasteAttachments = (attachments: FileAttachment[]) => {
		handleAttachmentAdd(attachments)
	}

	const handleAttachmentRetry = (id: string) => {
		const attachment = filterFileAttachments(getAttachments()).find((item) => item.id === id)
		if (attachment) {
			addTask(attachment)
		}
	}

	return {
		handleAttachmentAdd,
		handleAttachmentDelete,
		handleAttachmentUpdate,
		handleAttachmentSwap,
		handleAttachmentsDropped,
		handlePasteAttachments,
		handleAttachmentRetry,
		getAttachments,
	}
}
